import classNames from "classnames";
import React, {
  ChangeEventHandler,
  FocusEventHandler,
  forwardRef,
  useCallback,
} from "react";
import "./Slider.css";

interface SliderProps {
  id?: string;
  name?: string;
  value: string | number[] | string[];
  label?: string | JSX.Element;
  mode?: "single" | "range";
  min?: number;
  max?: number;
  step?: number;
  stepsLabels?: string[];
  error?: string | JSX.Element;
  onChange?: ChangeEventHandler<HTMLInputElement> | ((range: number[]) => void);
  onBlur?: FocusEventHandler<HTMLInputElement>;
}

export const Slider = forwardRef<HTMLInputElement, SliderProps>(
  (
    {
      id,
      name,
      label,
      value,
      mode = "single",
      min = 1,
      max = 5,
      step = 1,
      stepsLabels,
      onChange,
      onBlur,
    }: SliderProps,
    ref
  ) => {
    const percentageOffset = (value: number | undefined, relative: number) => {
      if (!value) {
        return 0;
      }
      return Math.ceil(Math.abs(relative - value) * (100 / (max - min))) - 1;
    };

    // Handler for label clicks
    const handleLabelClick = useCallback((labelIndex: number) => {
      if (mode === "single" && onChange) {
        const newValue = min + labelIndex * step;

        // Create a synthetic event to match the expected signature
        const syntheticEvent = {
          target: {
            value: newValue.toString()
          }
        } as React.ChangeEvent<HTMLInputElement>;

        (onChange as ChangeEventHandler<HTMLInputElement>)(syntheticEvent);
      }
    }, [min, step, mode, onChange]);

    // Create an array of step values for rendering markers and labels
    const stepValues = Array.from(
      { length: Math.floor((max - min) / step) + 1 },
      (_, i) => min + i * step
    );

    return (
      <div ref={ref} className="font-sans w-full">
        {label && (
          <label
            htmlFor={name}
            className="text-primary-800 dark:text-primary-200 mb-2 block text-sm font-medium"
          >
            {label}
          </label>
        )}

        <div className="relative pb-6">
          {/* Main slider input */}
          <div className="mb-2 relative">
            {mode === "single" ? (
              <>
                <input
                  data-cy={`${name}-range-input`}
                  id={id}
                  type="range"
                  value={value as unknown as number}
                  min={min}
                  max={max}
                  step={step}
                  onChange={onChange as ChangeEventHandler}
                  onBlur={onBlur}
                  className="slider h-2 w-full cursor-pointer appearance-none rounded-lg bg-primary-200 dark:bg-primary-700"
                />
              </>
            ) : (
              <div className="relative w-full">
                <div>
                  <input
                    id={`${id}-min`}
                    type="range"
                    value={value?.[0] || min}
                    min={min}
                    max={max}
                    step={step}
                    onChange={(e) => {
                      if (value?.[1] && parseInt(e.target.value) > value?.[1]) {
                        return;
                      }
                      const newRange = value;

                      (newRange as number[])?.splice(
                        0,
                        1,
                        parseInt(e.target.value)
                      );
                      // @ts-ignore
                      onChange &&
                        onChange(
                          [...(newRange as number[])].sort((a, b) => a - b)
                        );
                    }}
                    className="pointer-events-none absolute z-20 h-2 w-full cursor-pointer appearance-none opacity-0"
                  />
                  <input
                    id={`${id}-max`}
                    type="range"
                    value={value?.[1] || max}
                    min={min}
                    max={max}
                    step={step}
                    onChange={(e) => {
                      if (value?.[0] && parseInt(e.target.value) < value?.[0]) {
                        return;
                      }

                      const newRange = value;
                      (newRange as number[])?.splice(
                        1,
                        1,
                        parseInt(e.target.value)
                      );
                      // @ts-ignore
                      onChange &&
                        onChange(
                          [...(newRange as number[])].sort((a, b) => a - b)
                        );
                    }}
                    className="pointer-events-none absolute z-20 h-2 w-full cursor-pointer appearance-none opacity-0"
                  />
                  <div className="relative z-10 h-2">
                    <div className="absolute bottom-0 left-0 right-0 top-0 z-10 rounded-md bg-primary-200 dark:bg-primary-700" />
                    <div
                      className="absolute bottom-0 top-0 z-20 rounded-md bg-primary-500 dark:bg-primary-400"
                      style={{
                        right: `${percentageOffset(
                          value?.[1] as number,
                          max
                        )}%`,
                        left: `${percentageOffset(value?.[0] as number, min)}%`,
                      }}
                    />

                    <div
                      id={`${id}-handle-min`}
                      className="absolute top-1/2 z-30 -ml-3 -mt-3 h-6 w-6 -translate-y-1/2 rounded-full bg-primary-500 dark:bg-primary-400 shadow-md"
                      style={{
                        left: `${percentageOffset(value?.[0] as number, min)}%`,
                      }}
                    />
                    <div
                      id={`${id}-handle-max`}
                      className="absolute top-1/2 z-30 -mr-3 -mt-3 h-6 w-6 -translate-y-1/2 rounded-full bg-primary-500 dark:bg-primary-400 shadow-md"
                      style={{
                        right: `${percentageOffset(
                          value?.[1] as number,
                          max
                        )}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Step markers - improved for range mode */}
                <div className="flex justify-between px-0.5 relative -mt-1">
                  {stepValues.map((stepValue, index) => (
                    <div
                      key={stepValue}
                      className="z-20 h-1 w-1 rounded-full bg-primary-400 dark:bg-primary-500 absolute"
                      style={{
                        left: `${(index / (stepValues.length - 1)) * 100}%`,
                        transform: 'translateX(-50%)'
                      }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* Step labels - now clickable */}
          {stepsLabels?.length > 0 && (
            <div className="flex justify-between mt-4 mx-2 relative">
              {stepsLabels.map((label, index) => {
                const stepValue = min + index * step;
                const currentValue = mode === 'single'
                  ? parseInt(value as string)
                  : null;
                const isActive = currentValue === stepValue;

                return (
                  <div
                    key={`label-${stepValue}`}
                    className={classNames(
                      "text-center text-xs transition-colors absolute",
                      "cursor-pointer hover:text-primary-900 dark:hover:text-primary-100",
                      isActive
                        ? "text-primary-800 dark:text-primary-200 font-medium"
                        : "text-primary-500 dark:text-primary-400 font-normal"
                    )}
                    onClick={() => handleLabelClick(index)}
                    style={{
                      left: `${(index / (stepsLabels.length - 1)) * 100}%`,
                      transform: 'translateX(-50%)'
                    }}
                  >
                    {label}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }
);

Slider.displayName = "Slider";
